import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Check, Copy, Download, Loader2, Mail, Share2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useBlueprintCredits } from "@/hooks/useBlueprintCredits";
import {
  getOrCreateShare,
  loadShareSettings,
  requestSharePdf,
  saveShareSettings,
  sendShareEmail,
  shareCardImageUrl,
  shareUrl,
  type ShareSettings,
} from "@/lib/share";
import { ShareCampaignPanel } from "@/components/trends/ShareCampaignPanel";

type Props = {
  trendId: string;
  title: string;
  compact?: boolean;
};

const EXPIRY_OPTIONS = [
  { value: "0", label: "Never expires" },
  { value: "1", label: "1 day" },
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
];

/** Share a blueprint: public link, on-brand social preview, PDF, and email with expiry. */
export function ShareBlueprintButton({ trendId, title, compact = false }: Props) {
  const { user } = useAuth();
  const { balance, refetch: refetchCredits } = useBlueprintCredits(user?.id);
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | "link" | "pdf" | "settings" | "email">(null);
  const [copied, setCopied] = useState(false);
  const [cardKey, setCardKey] = useState(0);

  const [settings, setSettings] = useState<ShareSettings>({
    og_title: null,
    og_description: null,
    og_image_url: null,
    expires_at: null,
    is_active: true,
  });
  const [expiryChoice, setExpiryChoice] = useState("0");
  const [recipients, setRecipients] = useState("");
  const [note, setNote] = useState("");
  const [emailExpiry, setEmailExpiry] = useState("7");

  const ensureShare = async (): Promise<string | null> => {
    if (slug) return slug;
    if (!user) {
      toast.error("Sign in to share blueprints");
      return null;
    }
    setBusy("link");
    try {
      const { slug: next } = await getOrCreateShare(trendId, user.id, title);
      setSlug(next);
      return next;
    } catch (e) {
      toast.error((e as Error).message || "Could not create the share link");
      return null;
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    if (!slug) return;
    loadShareSettings(slug)
      .then((s) => {
        if (!s) return;
        setSettings(s);
        setExpiryChoice("0");
      })
      .catch(() => undefined);
  }, [slug]);

  const onOpenChange = async (next: boolean) => {
    setOpen(next);
    if (next) await ensureShare();
  };

  const copyLink = async () => {
    const s = slug ?? (await ensureShare());
    if (!s) return;
    await navigator.clipboard.writeText(shareUrl(s));
    setCopied(true);
    toast.success("Share link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadPdf = async () => {
    const s = slug ?? (await ensureShare());
    if (!s) return;
    setBusy("pdf");
    try {
      const result = await requestSharePdf(s);
      window.open(result.url, "_blank", "noopener,noreferrer");
      toast.success(
        result.cached
          ? "PDF ready — no credit used"
          : `PDF generated · 1 credit used${
              typeof result.credits_left === "number" ? ` · ${result.credits_left} left` : ""
            }`,
      );
      refetchCredits();
    } catch (e) {
      const err = e as Error & { code?: string };
      toast.error(
        err.code === "no_credits"
          ? "You're out of blueprint download credits — invite a friend to earn more"
          : err.code === "expired"
            ? "This link has expired — clear the expiry to re-enable it"
            : err.message,
      );
    } finally {
      setBusy(null);
    }
  };

  const savePreview = async () => {
    if (!slug) return;
    setBusy("settings");
    try {
      const expiresAt =
        expiryChoice === "0"
          ? null
          : new Date(Date.now() + Number(expiryChoice) * 86_400_000).toISOString();
      await saveShareSettings(slug, {
        og_title: settings.og_title,
        og_description: settings.og_description,
        og_image_url: settings.og_image_url,
        ...(expiryChoice !== "0" || settings.expires_at ? { expires_at: expiresAt } : {}),
      });
      setSettings((prev) => ({ ...prev, expires_at: expiresAt }));
      setCardKey((k) => k + 1);
      toast.success("Social preview saved");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const sendEmail = async () => {
    if (!slug) return;
    const list = recipients
      .split(/[,\s;]+/)
      .map((r) => r.trim())
      .filter(Boolean);
    if (list.length === 0) return toast.error("Add at least one recipient email");
    setBusy("email");
    try {
      const result = await sendShareEmail({
        slug,
        recipients: list,
        note: note || undefined,
        expiresInDays: emailExpiry === "0" ? null : Number(emailExpiry),
      });
      if (result.sent.length) {
        toast.success(
          `Sent to ${result.sent.length} recipient${result.sent.length === 1 ? "" : "s"}${
            result.expires_at
              ? ` · link expires ${new Date(result.expires_at).toLocaleDateString()}`
              : ""
          }`,
        );
        setRecipients("");
        setNote("");
        setSettings((prev) => ({ ...prev, expires_at: result.expires_at }));
      }
      result.failed.forEach((f) => toast.error(`${f.email}: ${f.error}`));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button
          className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-mono uppercase text-muted-foreground transition hover:text-foreground"
          aria-label={`Share blueprint: ${title}`}
        >
          <Share2 className="size-3" />
          {compact ? "share" : "share / pdf"}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Share this blueprint</DialogTitle>
          <DialogDescription>
            Anyone with the link sees a free read-only preview. The detailed blueprint and competitor
            overlay stay licensed.
          </DialogDescription>
        </DialogHeader>

        {busy === "link" && !slug ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Preparing link…
          </div>
        ) : !slug ? (
          <p className="text-sm text-muted-foreground">Sign in to create a share link.</p>
        ) : (
          <Tabs defaultValue="link">
            <TabsList className="w-full">
              <TabsTrigger value="link" className="flex-1">
                Link
              </TabsTrigger>
              <TabsTrigger value="preview" className="flex-1">
                Social preview
              </TabsTrigger>
              <TabsTrigger value="campaign" className="flex-1">
                Campaign / A/B
              </TabsTrigger>
              <TabsTrigger value="email" className="flex-1">
                Email
              </TabsTrigger>
            </TabsList>

            <TabsContent value="link" className="space-y-4 pt-4">
              <div className="flex gap-2">
                <Input readOnly value={shareUrl(slug)} aria-label="Public share link" />
                <Button variant="outline" onClick={copyLink} aria-label="Copy share link">
                  {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                </Button>
              </div>

              <div className="rounded-lg border border-border overflow-hidden bg-secondary/30">
                <img
                  key={cardKey}
                  src={`${shareCardImageUrl(slug)}&v=${cardKey}`}
                  alt={`Social preview card for ${title}`}
                  className="w-full"
                  loading="lazy"
                />
              </div>

              {settings.expires_at && (
                <p className="font-mono text-[11px] text-muted-foreground">
                  Link expires {new Date(settings.expires_at).toLocaleString()}
                </p>
              )}

              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="font-mono text-[11px] text-muted-foreground">
                  {balance === null ? "—" : `${balance} download credit${balance === 1 ? "" : "s"} left`}
                </p>
                <Button onClick={downloadPdf} disabled={busy === "pdf"}>
                  {busy === "pdf" ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Download className="size-4" />
                  )}
                  Download PDF
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="preview" className="space-y-4 pt-4">
              <div className="space-y-1.5">
                <Label htmlFor="og-title">Preview title</Label>
                <Input
                  id="og-title"
                  maxLength={90}
                  placeholder={title.slice(0, 80)}
                  value={settings.og_title ?? ""}
                  onChange={(e) => setSettings((p) => ({ ...p, og_title: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="og-desc">Preview description</Label>
                <Textarea
                  id="og-desc"
                  maxLength={200}
                  rows={3}
                  placeholder="Shown under the title when the link is pasted in Slack, X, LinkedIn…"
                  value={settings.og_description ?? ""}
                  onChange={(e) => setSettings((p) => ({ ...p, og_description: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="og-image">Preview image URL (https)</Label>
                <Input
                  id="og-image"
                  placeholder="https://yourbrand.com/og-card.png — leave blank for the generated card"
                  value={settings.og_image_url ?? ""}
                  onChange={(e) => setSettings((p) => ({ ...p, og_image_url: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">1200×630 works best.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="og-expiry">Link expiration</Label>
                <Select value={expiryChoice} onValueChange={setExpiryChoice}>
                  <SelectTrigger id="og-expiry">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPIRY_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={savePreview} disabled={busy === "settings"}>
                {busy === "settings" && <Loader2 className="size-4 animate-spin" />}
                Save preview
              </Button>
            </TabsContent>

            <TabsContent value="campaign" className="pt-4">
              <ShareCampaignPanel slug={slug} />
            </TabsContent>

            <TabsContent value="email" className="space-y-4 pt-4">
              <div className="space-y-1.5">
                <Label htmlFor="share-recipients">Recipients (up to 5, comma separated)</Label>
                <Input
                  id="share-recipients"
                  placeholder="alex@studio.com, sam@agency.io"
                  value={recipients}
                  onChange={(e) => setRecipients(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="share-note">Personal note (optional)</Label>
                <Textarea
                  id="share-note"
                  rows={3}
                  maxLength={400}
                  placeholder="Thought this one fits what you're building…"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email-expiry">Link expires after</Label>
                <Select value={emailExpiry} onValueChange={setEmailExpiry}>
                  <SelectTrigger id="email-expiry">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPIRY_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Applies to this share link for everyone, so details aren't exposed forever.
                </p>
              </div>
              <Button onClick={sendEmail} disabled={busy === "email"}>
                {busy === "email" ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Mail className="size-4" />
                )}
                Send blueprint
              </Button>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
